"use client";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FilterX, Globe2, LayoutGrid, Loader2, Lock, Pencil, Plus, Trash2, UserRound, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { useAppDialogs } from "@/components/dialogs/app-dialog-provider";
import { Button, ListCount, ListSurface, ListSurfaceBody, ListSurfaceHeader, ListToolbar, ListToolbarGroup, SkeletonTableRow, ToolbarIconButton, WorkspacePage } from "@/components/ds";
import { Card } from "@/components/ui-system";
import { tableHeaderSearchInputClass } from "@/components/ui-system/select";
import { cn } from "@/lib/utils/cn";
import { hasModulePermission, useEffectivePermissions } from "@/modules/auth/access";
import { useAuthStore } from "@/modules/auth/store";
import { listUsers } from "@/lib/api/endpoints/users";
import { createDashboard, deleteDashboard, listDashboards, updateDashboard } from "@/lib/api/endpoints/dashboards";
import { createDefaultHmiDocument, toDashboardLayout } from "../hmi/utils/default-document";
import { getDashboardLayoutKind } from "../hmi/utils/layout-kind";

import type { DashboardEditorState } from "../components/dashboard-management";
import {
  buildCreateEditorState,
  buildEditEditorState,
  canEditDashboardForUser,
  DashboardMetaModal,
  filterAssignableUsers,
} from "../components/dashboard-management";

export function DashboardListPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const dialogs = useAppDialogs();
  const tenantId = useAuthStore((s) => s.tenantId);
  const user = useAuthStore((s) => s.user);
  const impersonationStartedAt = useAuthStore((s) => s.impersonation?.started_at ?? null);
  const { permissionMap } = useEffectivePermissions();
  const canView = hasModulePermission(user, permissionMap, "dashboards", "view");
  const canCreate = hasModulePermission(user, permissionMap, "dashboards", "create");
  const canEdit = hasModulePermission(user, permissionMap, "dashboards", "edit");
  const canDelete = hasModulePermission(user, permissionMap, "dashboards", "delete");
  const canManage = canCreate || canEdit || canDelete;
  const [editor, setEditor] = useState<DashboardEditorState>({ open: false });
  const [editorError, setEditorError] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const dashboardsQuery = useQuery({
    queryKey: [
      "dashboards",
      tenantId ?? "",
      user?.id ?? "anon",
      user?.role ?? "no-role",
      impersonationStartedAt ?? "direct",
      canManage ? "manage" : "accessible",
    ],
    queryFn: () => listDashboards(tenantId!, canManage ? "manage" : "accessible"),
    enabled: Boolean(tenantId && canView),
    staleTime: 30_000,
  });

  const usersQuery = useQuery({
    queryKey: ["users", tenantId ?? "", user?.id ?? "anon", impersonationStartedAt ?? "direct", "dashboard-assignments"],
    queryFn: async () => (await listUsers(tenantId)).items,
    enabled: Boolean(tenantId && (canCreate || canEdit)),
    staleTime: 30_000,
  });

  const assignableUsers = useMemo(() => {
    return [...filterAssignableUsers(user, usersQuery.data ?? [])].sort((a, b) => {
      const nameA = (a.name ?? a.email).toLowerCase();
      const nameB = (b.name ?? b.email).toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [user, usersQuery.data]);

  const dashboards = dashboardsQuery.data ?? [];
  const filteredDashboards = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return dashboards;
    return dashboards.filter((dashboard) =>
      [dashboard.name, dashboard.description ?? "", dashboard.is_global ? "global" : "restricted"]
        .join(" ")
        .toLowerCase()
        .includes(term),
    );
  }, [dashboards, search]);

  const createMutation = useMutation({
    mutationFn: async (payload: {
      name: string;
      description: string;
      isGlobal: boolean;
      assignedUserIds: string[];
      dashboardType: "classic" | "hmi";
    }) => {
      return createDashboard({
        tenant_id: tenantId!,
        name: payload.name.trim(),
        description: payload.description.trim() || null,
        is_global: payload.isGlobal,
        assigned_user_ids: payload.isGlobal ? [] : payload.assignedUserIds,
        layout: payload.dashboardType === "hmi" ? toDashboardLayout(createDefaultHmiDocument()) : null,
      });
    },
    onSuccess: async (dashboard) => {
      await qc.invalidateQueries({ queryKey: ["dashboards", tenantId ?? ""] });
      setEditor({ open: false });
      setEditorError(null);
      navigate(`/dashboards/${dashboard.id}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (dashboardId: string) => {
      await deleteDashboard(dashboardId);
      return dashboardId;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["dashboards", tenantId ?? ""] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (payload: {
      dashboardId: string;
      name: string;
      description: string;
      isGlobal: boolean;
      assignedUserIds: string[];
    }) => {
      return updateDashboard(payload.dashboardId, {
        name: payload.name.trim(),
        description: payload.description.trim() || null,
        is_global: payload.isGlobal,
        assigned_user_ids: payload.isGlobal ? [] : payload.assignedUserIds,
      });
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["dashboards", tenantId ?? ""] });
      setEditor({ open: false });
      setEditorError(null);
    },
  });

  const editorSaving = createMutation.isPending || updateMutation.isPending;

  const submitEditor = async () => {
    if (!editor.open) {
      return;
    }
    const name = editor.name.trim();
    if (!name) {
      setEditorError("Dashboard name is required.");
      return;
    }
    if (!editor.isGlobal && editor.assignedUserIds.length === 0) {
      setEditorError("Select at least one user for a restricted dashboard.");
      return;
    }
    setEditorError(null);
    try {
      if (editor.mode === "create") {
        await createMutation.mutateAsync({
          name,
          description: editor.description,
          isGlobal: editor.isGlobal,
          assignedUserIds: editor.assignedUserIds,
          dashboardType: editor.dashboardType,
        });
      } else {
        await updateMutation.mutateAsync({
          dashboardId: editor.dashboardId!,
          name,
          description: editor.description,
          isGlobal: editor.isGlobal,
          assignedUserIds: editor.assignedUserIds,
        });
      }
    } catch (error) {
      setEditorError(error instanceof Error ? error.message : "Could not save dashboard.");
    }
  };

  const confirmDelete = async (dashboardId: string, dashboardName: string) => {
    const confirmed = await dialogs.confirm({
      title: `Delete "${dashboardName}"?`,
      description: "This removes the dashboard and all of its widgets.",
      confirmLabel: "Delete dashboard",
      tone: "danger",
    });
    if (!confirmed) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(dashboardId);
    } catch {
      // Mutation state surfaces any error on the next render path.
    }
  };

  if (!tenantId) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-lg flex-col items-center justify-center gap-ds-4 p-ds-8 text-center">
        <Card className="w-full p-ds-8">
          <p className="text-body text-muted">
            Your session has no active workspace. Pick one from the header selector or sign in with a workspace-enabled account.
          </p>
          <Link to="/login" className="btn btn-primary btn-sm mt-ds-4 !no-underline">
            Sign in
          </Link>
        </Card>
      </div>
    );
  }

  if (!canView) {
    return (
      <div className="mx-auto flex min-h-[60vh] max-w-xl flex-col items-center justify-center gap-ds-4 p-ds-8 text-center">
        <Card className="w-full p-ds-8">
          <p className="text-title text-foreground">Dashboard access is not enabled for your role</p>
          <p className="mt-ds-2 text-body text-muted">
            Your current permissions do not include access to the dashboards workspace.
          </p>
          <Link to="/overview" className="btn btn-secondary btn-sm mt-ds-4 !no-underline">
            Open overview
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <>
      {canCreate || canEdit ? (
        <DashboardMetaModal
          state={editor}
          saving={editorSaving}
          error={editorError}
          availableUsers={assignableUsers}
          onClose={() => {
            if (!editorSaving) {
              setEditor({ open: false });
              setEditorError(null);
            }
          }}
          onChange={(patch) => {
            if (!editor.open) {
              return;
            }
            setEditor({
              ...editor,
              ...patch,
              dashboardType: patch.dashboardType ?? editor.dashboardType,
              assignedUserIds:
                patch.isGlobal === true ? [] : (patch.assignedUserIds ?? editor.assignedUserIds),
            });
          }}
          onToggleAssignment={(userId) => {
            if (!editor.open) {
              return;
            }
            const assignedUserIds = editor.assignedUserIds.includes(userId)
              ? editor.assignedUserIds.filter((id) => id !== userId)
              : [...editor.assignedUserIds, userId];
            setEditor({ ...editor, assignedUserIds });
          }}
          onSubmit={() => void submitEditor()}
        />
      ) : null}

      <WorkspacePage className="pt-0">
        <main className="min-h-0 flex-1 overflow-y-auto">
          <ListSurface>
            <ListSurfaceHeader
              title="Dashboard library"
              description="Browse, assign, and open dashboard canvases from one workspace-aware list."
              meta={
                !dashboardsQuery.isLoading && !dashboardsQuery.isError && dashboards.length > 0 ? (
                  <ListCount
                    total={dashboards.length}
                    filtered={filteredDashboards.length}
                    noun={filteredDashboards.length === 1 ? "dashboard" : "dashboards"}
                  />
                ) : null
              }
              controls={
                <ListToolbar>
                  <ListToolbarGroup grow className="lg:max-w-[40%] lg:flex-[0_1_40%]">
                    <input
                      className={tableHeaderSearchInputClass}
                      placeholder="Search dashboard name or description"
                      value={search}
                      onChange={(event) => setSearch(event.target.value)}
                    />
                  </ListToolbarGroup>
                  {search.trim() ? (
                    <ToolbarIconButton icon={FilterX} label="Clear filters" onClick={() => setSearch("")} />
                  ) : null}
                  {canCreate ? (
                    <ToolbarIconButton
                      icon={Plus}
                      label="New dashboard"
                      variant="primary"
                      onClick={() => {
                        setEditorError(null);
                        setEditor(buildCreateEditorState());
                      }}
                    />
                  ) : null}
                </ListToolbar>
              }
            />
            <ListSurfaceBody className="overflow-x-auto">
              {dashboardsQuery.isLoading ? (
                <div className="p-ds-4" aria-label="Loading dashboards">
                  {Array.from({ length: 6 }, (_, index) => <SkeletonTableRow key={index} cols={5} />)}
                </div>
              ) : dashboardsQuery.isError ? (
                <div className="p-ds-4">
                  <p className="text-caption text-error" role="alert">
                    {dashboardsQuery.error instanceof Error ? dashboardsQuery.error.message : "Could not load dashboards."}
                  </p>
                </div>
              ) : dashboards.length === 0 ? (
                <div className="flex min-h-[20rem] flex-col items-center justify-center gap-ds-3 p-ds-8 text-center">
                  <LayoutGrid className="h-10 w-10 text-muted/35" aria-hidden />
                  <div>
                    <p className="text-title text-foreground">
                      {canCreate ? "No dashboards yet" : "No dashboards available"}
                    </p>
                    <p className="mt-ds-1 text-caption text-muted">
                      {canCreate
                        ? "Create one, then jump straight into its dedicated canvas."
                        : "You do not have any global or assigned dashboards yet."}
                    </p>
                  </div>
                  {canCreate ? (
                    <Button type="button" variant="primary" size="sm" onClick={() => setEditor(buildCreateEditorState())}>
                      Create dashboard
                    </Button>
                  ) : null}
                </div>
              ) : (
                <table className="min-w-full border-separate border-spacing-0">
                  <thead>
                    <tr className="bg-surface-secondary text-left">
                      <th className="border-b border-[var(--card-border)] px-ds-4 py-ds-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">
                        Dashboard
                      </th>
                      <th className="border-b border-[var(--card-border)] px-ds-4 py-ds-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">
                        Visibility
                      </th>
                      <th className="border-b border-[var(--card-border)] px-ds-4 py-ds-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">
                        Users
                      </th>
                      <th className="border-b border-[var(--card-border)] px-ds-4 py-ds-3 text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">
                        Updated
                      </th>
                      <th className="border-b border-[var(--card-border)] px-ds-4 py-ds-3 text-right text-[12px] font-semibold uppercase tracking-[0.08em] text-muted">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDashboards.map((dashboard, index) => {
                      const canEditDashboard = canEdit && canEditDashboardForUser(user, dashboard);
                      const canDeleteDashboard = canDelete && canEditDashboardForUser(user, dashboard);

                      return (
                        <tr key={dashboard.id} className={cn("align-middle hover:bg-surface-hover", index % 2 === 1 ? "bg-accent-muted/40" : "bg-surface-primary")}>
                          <td className="border-b border-[var(--card-border)] px-ds-4 py-ds-4">
                            <div className="min-w-[16rem]">
                              <div className="flex flex-wrap items-center gap-ds-2">
                                <button
                                  type="button"
                                  className="text-left text-body font-semibold text-foreground transition hover:text-[color:var(--accent)]"
                                  onClick={() => navigate(`/dashboards/${dashboard.id}`)}
                                >
                                  {dashboard.name}
                                </button>
                                <span
                                  className={`dashboard-pill ${
                                    getDashboardLayoutKind(dashboard.layout) === "hmi_canvas"
                                      ? "dashboard-pill-kind-hmi"
                                      : "dashboard-pill-kind-classic"
                                  }`}
                                >
                                  {getDashboardLayoutKind(dashboard.layout) === "hmi_canvas" ? "HMI" : "Classic"}
                                </span>
                              </div>
                              <p className="mt-ds-1 max-w-[22rem] truncate text-caption text-muted">
                                {dashboard.description?.trim() || "No description yet."}
                              </p>
                            </div>
                          </td>
                          <td className="border-b border-[var(--card-border)] px-ds-4 py-ds-4">
                            <span className={`dashboard-pill ${dashboard.is_global ? "dashboard-pill-global" : "dashboard-pill-restricted"}`}>
                              {dashboard.is_global ? <Globe2 className="h-3 w-3" aria-hidden /> : <Lock className="h-3 w-3" aria-hidden />}
                              {dashboard.is_global ? "Global" : "Restricted"}
                            </span>
                          </td>
                          <td className="border-b border-[var(--card-border)] px-ds-4 py-ds-4">
                            {dashboard.is_global ? (
                              <span className="text-caption text-muted">Role-based global access</span>
                            ) : (
                              <span className="dashboard-pill dashboard-pill-assigned !text-[12px] !normal-case !tracking-normal">
                                <UserRound className="h-3 w-3" aria-hidden />
                                {dashboard.assigned_user_ids.length} assigned
                              </span>
                            )}
                          </td>
                          <td className="border-b border-[var(--card-border)] px-ds-4 py-ds-4 text-caption text-muted">
                            {new Date(dashboard.updated_at).toLocaleDateString()}
                          </td>
                          <td className="border-b border-[var(--card-border)] px-ds-4 py-ds-4">
                            <div className="flex flex-nowrap items-center justify-end gap-ds-2 whitespace-nowrap">
                              <Button
                                type="button"
                                variant="secondary"
                                size="sm"
                                className="gap-ds-2 !border-primary/15 !bg-primary/10 !text-primary hover:!border-primary/25 hover:!bg-primary/15 hover:!text-primary"
                                onClick={() => navigate(`/dashboards/${dashboard.id}`)}
                              >
                                <Pencil className="h-4 w-4" aria-hidden />
                                Open
                              </Button>
                              {canEditDashboard ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="!h-9 !w-9 border !border-primary/15 !bg-primary/10 !px-0 !text-primary hover:!border-primary/25 hover:!bg-primary/15 hover:!text-primary"
                                  onClick={() => {
                                    setEditorError(null);
                                    setEditor(buildEditEditorState(dashboard));
                                  }}
                                  title={`Manage users for ${dashboard.name}`}
                                >
                                  <Users className="h-4 w-4" aria-hidden />
                                </Button>
                              ) : null}
                              {canEditDashboard ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="!h-9 !w-9 border !border-primary/15 !bg-primary/10 !px-0 !text-primary hover:!border-primary/25 hover:!bg-primary/15 hover:!text-primary"
                                  onClick={() => {
                                    setEditorError(null);
                                    setEditor(buildEditEditorState(dashboard));
                                  }}
                                  title={`Edit ${dashboard.name}`}
                                >
                                  <Pencil className="h-4 w-4" aria-hidden />
                                </Button>
                              ) : null}
                              {canDeleteDashboard ? (
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="!h-9 !w-9 border !border-[rgb(255_170_170)] !bg-[rgb(255_188_188)] !px-0 !text-[#7f1d1d] hover:!border-[rgb(207_81_81)] hover:!bg-[rgb(255_188_188)] hover:!text-[#7f1d1d]"
                                  disabled={deleteMutation.isPending && deleteMutation.variables === dashboard.id}
                                  onClick={() => void confirmDelete(dashboard.id, dashboard.name)}
                                  title={`Delete ${dashboard.name}`}
                                >
                                  {deleteMutation.isPending && deleteMutation.variables === dashboard.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                                  ) : (
                                    <Trash2 className="h-4 w-4" aria-hidden />
                                  )}
                                </Button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredDashboards.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-ds-4 py-ds-10 text-center text-caption text-muted">
                          No dashboards match the current search.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              )}
            </ListSurfaceBody>
          </ListSurface>
        </main>
      </WorkspacePage>
    </>
  );
}
